import React, { useEffect } from "react";
import { Box, Typography, List, ListItem, ListItemText } from "@mui/material";
import { useLocation } from "react-router-dom";

const TermsAndConditions = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0); // Scrolls to the top of the page
  }, [location.pathname]); // Triggers when the pathname changes

  return (
    <Box
      sx={{
        padding: "20px",
        maxWidth: "1100px",
        margin: "auto",
        marginTop: "60px",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Terms and Conditions
      </Typography>

      {/* 1. Definitions */}
      <Typography variant="h6" gutterBottom>
        1. Definitions
      </Typography>
      <List>
        {[
          "• 'We', 'Us', 'Our', 'Company': refers to UK TO MARS LTD.",
          "• 'You', 'Customer', 'Buyer': means the person, business or company placing an order with us.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 2. General */}
      <Typography variant="h6" gutterBottom>
        2. General
      </Typography>
      <List>
        {[
          "• By using our website and placing orders, you accept and agree to be bound by these terms and conditions.",
          "• Our primary service is the wholesale export and international transportation of goods from the United Kingdom.",
          "• We reserve the right to amend or update these terms at any time; changes will be posted on our website.",
          "• By using our services, you confirm that your intended use complies with all applicable import laws in the destination country.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 3. Orders and Acceptance */}
      <Typography variant="h6" gutterBottom>
        3. Orders and Acceptance
      </Typography>
      <List>
        {[
          "• All orders are subject to acceptance by us and product availability.",
          "• We reserve the right to refuse or cancel any order without prior notice.",
          "• Minimum order values may apply.",
          "• For export clients, we may require confirmation of import licensing, destination address verification, or end-user information before accepting the order.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 4. Pricing and Payment */}
      <Typography variant="h6" gutterBottom>
        4. Pricing and Payment
      </Typography>
      <List>
        {[
          "• All prices are quoted in GBP (£) unless otherwise agreed.",
          "• Prices exclude VAT, customs duties, and any applicable local taxes, unless stated otherwise.",
          "• Unless otherwise agreed in writing, 70% of the invoice must be paid in advance and 30% upon delivery and goods inspection.",
          "• Delays in final payment may result in withholding of export documents or delivery confirmation.",
          "• Payment methods accepted will be stated on our invoices and may include bank transfer, FIB, or other agreed methods.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 5. Delivery and Risk */}
      <Typography variant="h6" gutterBottom>
        5. Delivery and Risk
      </Typography>
      <List>
        {[
          "• Delivery dates and times are estimates and not guaranteed; delays may occur due to factors beyond our control.",
          "• Risk in the goods passes to you upon delivery or collection.",
          "• Unless otherwise stated, delivery terms are Ex Works (EXW) UK. Any agreed Incoterms (e.g., CIF, DDP) must be confirmed in writing before dispatch.",
          "• We offer UK and international delivery; specific delivery conditions (including carriage charges) are available upon request.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 6. Title and Ownership */}
      <Typography variant="h6" gutterBottom>
        6. Title and Ownership
      </Typography>
      <List>
        {[
          "• Title to goods remains with UK TO MARS LTD until full payment is received and cleared.",
          "• If goods are in transit or held under third-party carriers, ownership will remain with UK TO MARS LTD until final payment is confirmed.",
          "• Until title passes, you must store goods separately and not remove, deface, or obscure identifying marks.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 7. Inspection and Claims */}
      <Typography variant="h6" gutterBottom>
        7. Inspection and Claims
      </Typography>
      <List>
        {[
          "• You must inspect goods immediately upon delivery.",
          "• Any shortages, damages, or discrepancies must be reported to us in writing within 48 hours of receipt.",
          "• Returns or claims after this period may not be accepted.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 8. Returns and Refunds */}
      <Typography variant="h6" gutterBottom>
        8. Returns and Refunds
      </Typography>
      <List>
        {[
          "• Returns are only accepted by prior agreement and in accordance with our returns policy.",
          "• Goods must be returned in original condition, packaging unopened and within the agreed timeframe.",
          "• Refunds, credits, or replacements will be issued at our discretion after inspection.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 9. Product Information and Liability */}
      <Typography variant="h6" gutterBottom>
        9. Product Information and Liability
      </Typography>
      <List>
        {[
          "• Product descriptions, images, and specifications are provided in good faith and may be subject to change without notice.",
          "• We accept no liability for losses resulting from incorrect use, storage, or onward sale of goods.",
          "• Our liability is limited to the value of the goods supplied and does not extend to consequential loss.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 10. Export & International Trade */}
      <Typography variant="h6" gutterBottom>
        10. Export & International Trade
      </Typography>
      <List>
        {[
          "• UK TO MARS LTD specialises in international wholesale and export transportation services.",
          "• We provide all standard export documentation, including commercial invoices, packing lists, and transport labels.",
          "• Buyers are responsible for ensuring goods are lawfully imported, including securing any necessary import licenses or tax clearance.",
          "• Goods exported from the UK are generally zero-rated for VAT purposes. Buyers must ensure correct VAT treatment in the destination country.",
          "• We accept no responsibility for delays or confiscation caused by incorrect or missing import documentation on the customer's side.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 11. Compliance */}
      <Typography variant="h6" gutterBottom>
        11. Compliance
      </Typography>
      <List>
        {[
          "• Both parties must comply with all applicable UK and international laws, regulations, and sanctions.",
          "• You agree not to use our goods for unlawful purposes or resell into restricted territories.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 12. Data Protection */}
      <Typography variant="h6" gutterBottom>
        12. Data Protection
      </Typography>
      <List>
        {[
          "• We comply with the UK GDPR and Data Protection Act 2018.",
          "• If customer data is transferred outside the UK (e.g., via cloud platforms for logistics or invoicing), we ensure appropriate safeguards are in place.",
          "• For more information, refer to our Privacy Policy.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 13. Force Majeure */}
      <Typography variant="h6" gutterBottom>
        13. Force Majeure
      </Typography>
      <List>
        {[
          "• We shall not be liable for delay or failure in performance due to events beyond our reasonable control (including natural disasters, transport delays, customs intervention, strikes, etc.).",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 14. Jurisdiction */}
      <Typography variant="h6" gutterBottom>
        14. Jurisdiction
      </Typography>
      <List>
        {[
          "• These terms are governed by the laws of England and Wales.",
          "• For international customers, any disputes arising will be governed by the laws of England and Wales unless otherwise agreed in writing.",
          "• Disputes arising will be subject to the exclusive jurisdiction of the English courts.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>

      {/* 15. Contact Information */}
      <Typography variant="h6" gutterBottom>
        15. Contact Information
      </Typography>
      <Typography variant="body1" paragraph>
        For questions, claims, or support, contact:
      </Typography>
      <Typography variant="body1" paragraph>
        UK TO MARS LTD
        <br />
        15 Little Balmer, Buckingham Industrial Estate, Buckingham, MK18 1TF, UK
        <br />
        Email: info@uktomars.co.uk
        <br />
        Phone: +44 7564 627910
      </Typography>

      {/* 16. International Transport Services */}
      <Typography variant="h6" gutterBottom>
        16. International Transport Services
      </Typography>
      <List>
        {[
          "• We offer consolidated and full-load shipping options by land. Transit times may vary by route and are estimated at the time of booking.",
          "• Any shipping schedules or routes are indicative only. UK TO MARS LTD shall not be held liable for delays due to customs, port congestion, or third-party carriers.",
          "• Customers must ensure availability to receive and inspect goods upon delivery at the agreed location.",
        ].map((item, index) => (
          <ListItem key={index} sx={{ padding: "0" }}>
            <ListItemText primary={item} />
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default TermsAndConditions;
